import React, {useEffect, useState} from 'react';
import {connect} from 'react-redux';
import {Button, Dropdown, Icon, Page, Row, Table, TextInput, Tile} from '../../../shared';
import debug from 'debug';
import axiosInstance from '../../../shared/utils/axios';
import {_openModal, _setReportTableReset, isEntitled} from '../../../shared/store';
import Details from '../../../shared/pages/InfoPanels/Details';
import PropTypes from 'prop-types';
import {gatherData, getUpdatedFilter, openDocument, setSectionURI} from '../../../shared/utils/utils';
import history from '../../../history';
import useQueryString from '../../../shared/utils/useQueryString';
import {
  AdvisorTransactionsReport,
  CashBalancesReport,
  ManuallyCreatedAssetsReport,
  ModularReport,
  PreviouslyMappedAccountCodesReport,
  RiskReport,
  TotaledHoldingsReport,
  UserActivityLogReport,
  AdvisorViewInvestorExperienceActivity,
} from '../../components/reports';
import {withTheme} from 'styled-components/macro';
import Tracker from '../../../shared/components/Tracker';

const gatherDocuments = gatherData('/documents/library');

// TODO Verify Report Entitlements prevent from being displayed.
const reportMap = {
  'Advisor Transactions': AdvisorTransactionsReport,
  'Balance Report': ModularReport,
  'Cash Balances': CashBalancesReport,
  'General Report': ModularReport,
  'Exposure Report': ModularReport,
  'Holdings Report': ModularReport,
  'Manually Created Assets Report': ManuallyCreatedAssetsReport,
  'Performance Report': ModularReport,
  'Previously Mapped Account Codes': PreviouslyMappedAccountCodesReport,
  'Risk Report': RiskReport,
  'Totaled Holdings Report': TotaledHoldingsReport,
  'User Activity Log Report': UserActivityLogReport,
  'Advisor View Investor Experience Activity': AdvisorViewInvestorExperienceActivity,
  'Income & Expense Report': ModularReport,
  'Net Worth Report': ModularReport,
};

// TODO Implement retrying batch report
/*
return $http.get(NODEJS_API_URL + 'reports/batches/' + batchID + '/retry')
        .then(function success(res) {
          var report = lazy(Master.reports).findWhere({id: $scope.batch.reportID});
          var batchContext = {
            containerIDs: res.data.containers,
            dates: res.data.dates,
            canShare: res.data.canShare,
          };
          $scope.$dismiss();
          return alert('Moved to V3');
          // return BatchReportsAdvisorModal.open({report: report}, batchContext); // Moved to V3 as ModularReport.js
        });_img
 */

// TODO Implement 'jumping' to specific report

const log = debug('circleblack:report');

function Report(props) {
  log('rendering');
  const {openModal, setReportTableReset, tableKey, theme} = props;
  const [reports, setReports] = React.useState([]);
  const [report, setReport] = React.useState(null);
  const [preselectedReport] = useQueryString('preselectedReport');

  const [allReports, setAllReports] = React.useState([]);
  const [filters, setFilters] = useState({});
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [activeInfoPanel, setActiveInfoPanel] = useState('details');
  const infoPanelComponents = {details: Details};
  const infoPanelTitleMapping = {details: 'Details'};

  const defaultParams = {
    cat: 'Report',
  };
  const dataManager = {
    gatherData: inputValue => gatherDocuments({
      ...defaultParams,
      ...getUpdatedFilter(filters),
      ...inputValue,
    }),
    updateActiveDocument: document => setSelectedDocument(document),
  };

  const tableReset = () => {
    setFilters({});
    dataManager.refresh();
  };

  useEffect(() => {
    setReportTableReset(tableReset);
  }, []);

  const updateFilter = (name, value) => {
    setFilters({
      ...filters,
      [name]: value,
    });
    dataManager.refresh();
  };

  const getInfoPanel = () => {
    // const {selectedDocument, activeInfoPanel, infoPanelComponents} = this.state;
    const InfoPanel = infoPanelComponents[activeInfoPanel];
    return {
      title: infoPanelTitleMapping[activeInfoPanel],
      content: <InfoPanel document={selectedDocument} dataManager={dataManager}/>,
      rowID: selectedDocument ? selectedDocument.id : null,
      setRow: doc => setSelectedDocument(doc),
    };
  };

  const shareRender = document => <Icon svg={'circleShare'} action={() => {
    history.push(setSectionURI(`/${document.id}/sharing`));
  }} disabled={(document.notShareable || document.cat === 'Report' && !document.assocHouseholdID)}/>;

  const detailsRender = document => <Icon svg={'details'} action={() => {
    setActiveInfoPanel('details');
    setSelectedDocument(document);
    dataManager.toggleDisplayInfoPanel(true);
  }}/>;

  function launchReport(name, option) {
    return axiosInstance.get(`/reports/${option.value}`)
      .then(res => setReport(res.data))
      .catch(err => openModal({type: 'Error', err}));
  }

  if (!reports.length || !allReports.length) {
    axiosInstance.get('/reports')
      .then(res => {
        const mapped = res.data
          .sort((a, b) => {
            if (a.name === b.name) {
              return 0;
            }

            return a.name > b.name ? 1 : -1;
          })
          .map(o => ({
            label: o.name,
            value: o.name,
          }));

        setAllReports(mapped);
        setReports(mapped.filter(o => !!reportMap[o.label]));

        if (preselectedReport) {
          launchReport('preselectedReport', {value: preselectedReport});
        }
      })
      .catch(err => openModal({type: 'Error', err}));
  }

  function reportMapper(reportName) {
    const ReportBuilder = reportMap[reportName];
    if (!ReportBuilder) {
      log('Not yet implemented', reportName);
      return;
    }
    return <ReportBuilder report={report} cancelAction={setReport}/>;
  }

  return <Page>
    <Row wrap={true} alignSubRow={'stretch'} alignItems={'stretch'}>
      <Tile key='Generate' title='Generate' minWidth={'min(400px, calc(100vh - 20px))'} width={'30%'} layout={{grow: 1}}>
        <Row wrap={true} justifySubRow={'space between'}>
          {report && <TextInput type={'text'}
                                title={'Report Type'}
                                placeholder={'Select Report'}
                                dataPoint={{name: 'reportType', value: report.name}}
                                onChange={() => {}}
                                width={'100px'}
                                layout={{grow: 1}}
                                readOnly={true}/>}
          {!report && <Dropdown placeholder={'Select A Report...'}
                                dataPoint={{name: 'reportType'}}
                                onChange={launchReport}
                                layout={{grow: 1}}
                                width={'auto'}
                                options={reports}/>}
          {!report && <Button importance={'secondary'}
                              size={'md'}
                              label={'Pending Reports'}
                              disabled={false}
                              width={'fit-content'}
                              action={() => openModal({componentName: 'PendingReports'})}/>}
          {report && <Button importance={'secondary'}
                             width={'auto'}
                             size={'md'}
                             label={'Change Report'}
                             action={() => setReport(null)}/>}
        </Row>
        {report && reportMapper(report.name)}
        {!report && isEntitled([{group: 'fakeDoors', feature: 'scheduleReports', access: 'r'}]) && <Row>
          <Tracker category={'fakeDoor'} feature={'scheduleReports'} tracker={'int-reportSched-fd'}/>
          <Button importance={'secondary'}
                  size={'md'}
                  label={'Schedule Report'}
                  disabled={false}
                  width={'fit-content'}
                  action={() => history.push('/preview/scheduleReports?origin=int-reportSched-fd')}/>
        </Row>}
      </Tile>
      <Tile key={'view'} title='Reports' minWidth={'400px'} width={'60%'} layout={{grow: 1}}>
        <Table key={tableKey}
               dataManager={dataManager}
               columns={[
                 {
                   title: 'Document Name',
                   dataPoint: 'title',
                   type: 'text',
                   minWidth: 160,
                   isSortable: true,
                 },
                 {
                   title: 'Type',
                   dataPoint: 'type',
                   type: 'text',
                   minWidth: 60,
                   isSortable: true,
                 },
                 {
                   title: 'Date',
                   dataPoint: 'createdAt',
                   type: 'datetime',
                   minWidth: 60,
                   isSortable: true,
                 },
                 {
                   title: 'Share',
                   dataPoint: 'isShared',
                   render: shareRender,
                   type: 'custom',
                   minWidth: 50,
                   isSortable: false,
                 },
                 {
                   title: 'Details',
                   dataPoint: 'details',
                   render: detailsRender,
                   type: 'custom',
                   minWidth: 50,
                   isSortable: false,
                 },
               ].filter(r => !!r)}
               defaultSort={{
                 column: 'createdAt',
                 desc: true,
               }}
               searches={[
                 {
                   title: 'Document Name',
                   name: 'title',
                   isFuzzy: true,
                 },
               ]}
               filterValues={filters}
               filters={[
                 <Dropdown
                   key={'reportType'}
                   title={'Report Type'}
                   placeholder={'Select report type'}
                   type={'dropdown'}
                   dataPoint={{
                     name: 'type',
                   }}
                   options={allReports}
                   onChange={updateFilter}
                   isClearable/>,
               ]}
               iconsKey={[
                 {
                   title: 'Recent Approval',
                   image: 'checkMarkCircleO',
                   color: theme.dark ? '#99FA66' : '#79AF30',
                   description: 'The content was recently approved for sharing with households.',
                   category: 'Share Status',
                   displayCondition: d => !!d.recentlyApproved,
                 },
                 {
                   title: 'Recent Denial',
                   image: 'exclamationCircle',
                   color: theme.dark ? '#FFCC03' : '#CF790B',
                   description: 'The content was recently denied from being shared with households.',
                   category: 'Share Status',
                   displayCondition: d => !!d.recentlyDenied,
                 },
                 {
                   title: 'Pending Approval',
                   image: 'hourglass',
                   color: '#7D84C0',
                   description: 'The content was recently attempted to be shared with households and is still pending review.',
                   category: 'Share Status',
                   displayCondition: d => !!d.pendingApproval,
                 },
                 {
                   title: 'Past Approval',
                   image: 'checkMarkCircleO',
                   color: '#9B9B9B',
                   description: 'The content has previously been approved and shared with households.',
                   category: 'Share Status',
                   displayCondition: d => !!d.previouslyApproved && !d.recentlyApproved,
                 },
                 {
                   title: 'New Content',
                   image: 'dotNew',
                   color: '#00D0BE',
                   description: 'The content has newly been added to the library.',
                   category: 'General',
                   displayCondition: d => !!d.isNew,
                 },
               ]}
               rowIdentifier={'id'}
               rowOnClick={(row) => openDocument(row, dataManager.refresh)}
               infoPanel={getInfoPanel()}/>
      </Tile>
    </Row>
  </Page>;
}

const mapStateToProps = state => ({
  tableKey: state.badges.tableKey,
});

const mapDispatchToProps = dispatch => ({
  openModal: modalToOpen => dispatch(_openModal(modalToOpen)),
  setReportTableReset: func => dispatch(_setReportTableReset(func)),
});

export default withTheme(connect(mapStateToProps, mapDispatchToProps)(Report));

Report.propTypes = {
  /**
   * The open function that dispatches _openModal from Redux
   */
  openModal: PropTypes.func,
  /**
   * The function that dispatches _setReportTableRefresh from Redux
   */
  setReportTableReset: PropTypes.func,
  /**
   * From badges reducer
   */
  tableKey: PropTypes.func,
  theme: PropTypes.object,
};
